import { useState, useEffect, useCallback } from 'react';
import { UpgradePrompt, UpgradeTrigger } from './UpgradePrompt';
import { onQuotaExceeded, QuotaExceededEvent } from '../../services/api';
import { useAuthStore } from '../../store/auth.store';
import { trackUpgradeCtaClicked } from '../../services/analytics';

/**
 * QuotaWall — global component that listens for quota_exceeded events
 * and shows the appropriate modal:
 *
 * - reason="registration_required": anonymous user, needs to sign up
 * - reason="limit_reached": registered user hit limit → show referral CTA
 * - reason="upgrade_required": needs paid plan (Phase 3)
 *
 * Mount this once at the app root level (e.g., in App.tsx or Layout).
 */
export function QuotaWall() {
  const [isOpen, setIsOpen] = useState(false);
  const [quotaEvent, setQuotaEvent] = useState<QuotaExceededEvent | null>(null);
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    const unsubscribe = onQuotaExceeded((event) => {
      setQuotaEvent(event);
      setIsOpen(true);
    });
    return unsubscribe;
  }, []);

  const handleUpgrade = useCallback(() => {
    trackUpgradeCtaClicked('quota_wall', {
      reason: quotaEvent?.reason || '',
      operation: quotaEvent?.operation || '',
      tier: quotaEvent?.tier || '',
    });
    if (!isAuthenticated) {
      window.open('/register', '_blank', 'noopener');
    } else if (quotaEvent?.reason === 'upgrade_required') {
      window.open('/pricing', '_blank', 'noopener');
    } else if (quotaEvent?.reason === 'limit_reached') {
      // Navigate to referral page to invite friends for more credits
      window.location.href = '/app/referral';
    } else {
      setIsOpen(false);
    }
  }, [isAuthenticated, quotaEvent]);

  const handleDismiss = useCallback(() => {
    setIsOpen(false);
  }, []);

  if (!quotaEvent) return null;

  // Map quota event reason to UpgradePrompt trigger
  let trigger: UpgradeTrigger = 'progressive';
  if (quotaEvent.reason === 'registration_required') {
    trigger = 'cv_limit';
  } else if (quotaEvent.reason === 'upgrade_required') {
    trigger = 'upgrade_paid';
  } else if (quotaEvent.reason === 'limit_reached') {
    trigger = 'progressive';
  }

  return (
    <UpgradePrompt
      variant="modal"
      trigger={trigger}
      quotaInfo={{
        remaining: Math.max(0, quotaEvent.limit - quotaEvent.used),
        limit: quotaEvent.limit,
        type: quotaEvent.operation,
      }}
      onUpgrade={handleUpgrade}
      onDismiss={handleDismiss}
      isOpen={isOpen}
    />
  );
}
